import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ApiService} from './api.service';
import {Filter} from '../../models/filter.model';

/**
 * Service for managing import filters
 * This service handles the creation, retrieval, update, and deletion of filters
 */
@Injectable({
  providedIn: 'root'
})
export class FilterService {
    private apiService = inject(ApiService);


  /**
   * Retrieves all filters for a client
   * @param clientId The client ID to retrieve filters for
   * @returns An Observable with an array of filters
   */
  getFilters(clientId: string): Observable<Filter[]> {
    return this.apiService.get<Filter[]>('filters', { clientId });
  }

  /**
   * Retrieves a specific filter by its ID
   * @param id The filter ID
   * @param clientId The client ID to retrieve the filter for
   * @returns An Observable with the filter
   */
  getFilterById(id: string, clientId: string): Observable<Filter> {
    return this.apiService.get<Filter>(`filters/${id}`, { clientId });
  }

  /**
   * Creates a new filter
   * @param filter The filter to create (without id, createdAt, and updatedAt)
   * @param clientId The client ID to create the filter for
   * @returns An Observable with the created filter
   */
  createFilter(filter: Omit<Filter, 'id' | 'createdAt' | 'updatedAt'>, clientId: string): Observable<Filter> {
    return this.apiService.post<Filter>('filters', filter, { clientId });
  }

  /**
   * Updates an existing filter
   * @param id The filter ID
   * @param filter The updated filter properties
   * @param clientId The client ID to update the filter for
   * @returns An Observable with the updated filter
   */
  updateFilter(id: string, filter: Partial<Filter>, clientId: string): Observable<Filter> {
    return this.apiService.put<Filter>(`filters/${id}`, filter, { clientId });
  }

  /**
   * Deletes a filter
   * @param id The filter ID
   * @param clientId The client ID to delete the filter for
   * @returns An Observable with the deletion result
   */
  deleteFilter(id: string, clientId: string): Observable<any> {
    return this.apiService.delete<any>(`filters/${id}`, { clientId });
  }

  /**
   * Runs a filter to import matching companies
   * @param id The filter ID
   * @param clientId The client ID to run the filter for
   * @returns An Observable with the filter run result
   */
  runFilter(id: string, clientId: string): Observable<any> {
    return this.apiService.post<any>(`filters/${id}/run`, {}, { clientId });
  }
}
